import type {
  BinderDateValue,
  BinderEntryType,
  BinderTemplateId,
  FactValueType,
} from "./binder-api.js";

/**
 * Client-side Property File template catalog. The fact `key`s mirror the
 * canonical keys in the backend template registry
 * (customer/api/src/domain/property-binder.ts BINDER_TEMPLATES); this adds the
 * display metadata (labels, units, select options) the editor needs to render
 * suggested rows. Templates suggest fields — they never gate.
 */

export interface TemplateField {
  key: string;
  label: string;
  valueType: FactValueType;
  unit?: string;
  options?: string[];
}

export interface BinderTemplate {
  id: BinderTemplateId;
  label: string;
  fields: TemplateField[];
  /** Date kinds worth suggesting for this template (keys from BINDER_DATE_KINDS). */
  suggestedDateKeys: string[];
}

export const BINDER_TEMPLATES: BinderTemplate[] = [
  {
    id: "water_heater",
    label: "Water heater",
    fields: [
      { key: "capacity", label: "Capacity", valueType: "number", unit: "gal" },
      { key: "fuelType", label: "Fuel type", valueType: "select", options: ["gas", "electric", "propane"] },
      { key: "brand", label: "Brand", valueType: "text" },
      { key: "model", label: "Model", valueType: "text" },
      { key: "serialNumber", label: "Serial number", valueType: "text" },
    ],
    suggestedDateKeys: ["installed"],
  },
  {
    id: "air_filter",
    label: "Air filter",
    fields: [
      { key: "filterSize", label: "Filter size", valueType: "text" },
      { key: "quantity", label: "Quantity", valueType: "number" },
      { key: "changeInterval", label: "Change interval", valueType: "text" },
    ],
    suggestedDateKeys: ["lastServiced"],
  },
  {
    id: "fixture",
    label: "Fixture",
    fields: [
      { key: "room", label: "Room", valueType: "text" },
      { key: "fixtureType", label: "Fixture type", valueType: "text" },
      { key: "finish", label: "Finish", valueType: "text" },
      { key: "bulbType", label: "Bulb type", valueType: "text" },
      { key: "brand", label: "Brand", valueType: "text" },
      { key: "model", label: "Model", valueType: "text" },
    ],
    suggestedDateKeys: [],
  },
  {
    id: "yard_note",
    label: "Yard note",
    fields: [
      { key: "area", label: "Area", valueType: "text" },
      { key: "productUsed", label: "Product used", valueType: "text" },
      { key: "quantity", label: "Quantity", valueType: "text" },
    ],
    suggestedDateKeys: ["activityDate"],
  },
  {
    id: "maintenance_note",
    label: "Maintenance",
    fields: [
      { key: "activity", label: "Activity", valueType: "text" },
      { key: "performedBy", label: "Performed by", valueType: "text" },
      { key: "cost", label: "Cost", valueType: "text" },
    ],
    suggestedDateKeys: ["activityDate"],
  },
];

export function findTemplate(id: BinderTemplateId | undefined): BinderTemplate | undefined {
  return id ? BINDER_TEMPLATES.find((t) => t.id === id) : undefined;
}

/** Date kinds the entry editor offers. Keys are stored verbatim in `dates`. */
export const BINDER_DATE_KINDS: { key: string; label: string }[] = [
  { key: "installed", label: "Installed" },
  { key: "purchased", label: "Purchased" },
  { key: "replaced", label: "Replaced" },
  { key: "lastServiced", label: "Last serviced" },
  { key: "activityDate", label: "Activity date" },
  { key: "observedDate", label: "Observed" },
];

export function dateKindLabel(key: string): string {
  return BINDER_DATE_KINDS.find((d) => d.key === key)?.label ?? key;
}

export const entryTypeLabels: Record<BinderEntryType, string> = {
  asset: "Appliance",
  note: "Note",
  maintenance_log: "Maintenance",
  project_reference: "Project",
};

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Human-friendly approximate date, e.g. "≈2018", "Apr 2026", "Apr 15, 2026". */
export function formatApproxDate(d: BinderDateValue): string {
  const prefix = d.estimated ? "≈" : "";
  if (d.precision === "year") return `${prefix}${d.value}`;
  if (d.precision === "month") {
    const [y, m] = d.value.split("-");
    const mi = Number(m) - 1;
    return `${prefix}${MONTHS[mi] ?? m} ${y}`;
  }
  const [y, m, day] = d.value.split("-");
  const mi = Number(m) - 1;
  return `${prefix}${MONTHS[mi] ?? m} ${Number(day)}, ${y}`;
}

/**
 * Derived age for display only (the date + precision stay the source of truth).
 * Returns e.g. "about 8 years old", "less than a year old", or null if the date
 * is in the future or unparseable.
 */
export function deriveAge(d: BinderDateValue, now: Date = new Date()): string | null {
  const year = Number(d.value.slice(0, 4));
  if (!Number.isFinite(year)) return null;
  const month = d.precision === "year" ? 0 : Number(d.value.slice(5, 7)) - 1;
  const day = d.precision === "day" ? Number(d.value.slice(8, 10)) : 1;
  const then = new Date(year, Number.isFinite(month) ? month : 0, Number.isFinite(day) ? day : 1);
  if (then.getTime() > now.getTime()) return null;
  let years = now.getFullYear() - then.getFullYear();
  const beforeAnniversary =
    now.getMonth() < then.getMonth() ||
    (now.getMonth() === then.getMonth() && now.getDate() < then.getDate());
  if (beforeAnniversary) years -= 1;
  if (years <= 0) return "less than a year old";
  return `about ${years} ${years === 1 ? "year" : "years"} old`;
}
